import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabaseService, type PullRequestDetails } from '@/shared/services/supabaseService';
import { LoadingSpinner } from '@/ui/LoadingSpinner';
import PullRequestHeader from '@/features/pull-request/components/PullRequestHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/tabs';
import DiffView from '@/features/pull-request/components/DiffView';
import TimelineView from '@/features/pull-request/components/TimelineView';
import { PageLayout, PageContent } from '@/components/PageLayout';
import { Alert, AlertDescription, AlertTitle } from '@/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/ui/breadcrumb';
import { useDiff } from '@/shared/hooks/useDiff';


const PullRequestPage: React.FC = () => {
  const { repoId, prNumber } = useParams<{ repoId: string; prNumber: string }>();

  const { data: pr, isLoading: isLoadingPR, isError: isErrorPR, error: prError } = useQuery<PullRequestDetails | null, Error>({
    queryKey: ['pullRequest', repoId, prNumber],
    queryFn: () => {
      if (!repoId || !prNumber) {
        return Promise.resolve(null);
      }
      return supabaseService.getPullRequestByNumber(repoId, parseInt(prNumber, 10));
    },
    enabled: !!repoId && !!prNumber,
  });

  const { data: diffData, isLoading: isLoadingDiff, isError: isErrorDiff, error: diffError } = useDiff(repoId || '', parseInt(prNumber || '0', 10));

  if (isLoadingPR) {
    return <div className="flex justify-center items-center h-full"><LoadingSpinner /></div>;
  }

  if (isErrorPR) {
    return (
      <PageLayout>
        <PageContent>
          <Alert variant="destructive">
            <AlertTitle>Error loading pull request</AlertTitle>
            <AlertDescription>{prError.message}</AlertDescription>
          </Alert>
        </PageContent>
      </PageLayout>
    );
  }

  if (!pr) {
    return <div>Pull request not found.</div>;
  }

  const repoName = pr.repo_url ? new URL(pr.repo_url).pathname.substring(1) : 'Repository';

  return (
    <PageLayout>
      <PageContent>
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/repositories">Repositories</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/repository/${repoId}`}>{repoName}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <span>Pull Request #{prNumber}</span>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <PullRequestHeader pr={pr} />
        <Tabs defaultValue="diff" className="w-full mt-4">
          <TabsList>
            <TabsTrigger value="diff">Diff</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>
          <TabsContent value="diff">
            {isLoadingDiff && <LoadingSpinner />}
            {isErrorDiff && (
              <Alert variant="destructive">
                <AlertTitle>Error loading diff</AlertTitle>
                <AlertDescription>{diffError.message}</AlertDescription>
              </Alert>
            )}
            {diffData && <DiffView files={diffData.files} rawDiff={diffData.rawDiff} />}
          </TabsContent>
          <TabsContent value="timeline">
            <TimelineView pr={pr} />
          </TabsContent>
        </Tabs>
      </PageContent>
    </PageLayout>
  );
};

export default PullRequestPage;
